import type TauriDatabase from "@tauri-apps/plugin-sql";
import { type Database as BunDatabase } from "bun:sqlite";
import { z } from "zod";

import { type BaseSpec, baseSpecSchema } from "@moment/api-collab/api-types";

import { type SqlString, sql } from "../SqlString";
import { type SqliteTableDef } from "../schema";
import { canonicalJsonString } from "../types/canonicalJsonString";
import { createTableSql } from "./createTableSql";

export const BASE_METADATA_TABLE_NAME = "__base_metadata";
export const BASE_METADATA_SINGLETON_RECORD_ID = 1;

export const baseMetadataRow = z.object({
	id: z.number(),
	epoch: z.number(),
	baseSpec: baseSpecSchema,
	updatedAt: z.string(),
});
export type BaseMetadataRow = z.infer<typeof baseMetadataRow>;

/**
 * The table definition for the base metadata table. This table stores information
 * about the current state of the base, including the current epoch.
 */
export const baseMetadataTableDef: SqliteTableDef = {
	tableName: BASE_METADATA_TABLE_NAME,
	columns: [
		{
			name: "id",
			type: "INTEGER",
			primaryKey: true,
			nullable: false,
			defaultValue: sql.lit(1),
		},
		{ name: "epoch", type: "INTEGER", nullable: false, defaultValue: 0 },
		{ name: "baseSpec", type: "TEXT", nullable: false },
		{
			name: "updatedAt",
			type: "DATETIME",
			nullable: false,
			defaultValue: sql.lit("CURRENT_TIMESTAMP"),
		},
	],
};

/**
 * Creates a SQL statement that creates the base metadata table.
 */
export function createBaseMetadataTableSql(): SqlString {
	return createTableSql(baseMetadataTableDef, true);
}

export async function runEnsureBaseMetadataTable(
	db: BunDatabase | TauriDatabase,
	baseSpec?: BaseSpec
) {
	const { statement, params } = createBaseMetadataTableSql();

	if ("prepare" in db) {
		// For Bun SQLite
		db.prepare("BEGIN TRANSACTION").run();
		try {
			db.prepare(statement, params).run();
			if (baseSpec) {
				const initSql = createInitialBaseMetadataSql(baseSpec);
				db.prepare(initSql.statement, initSql.params).run();
			}
			db.prepare("COMMIT").run();
		} catch (error) {
			db.prepare("ROLLBACK").run();
			throw error;
		}
	} else {
		// For Tauri SQLite
		await db.execute("BEGIN TRANSACTION");
		try {
			await db.execute(statement, params);
			if (baseSpec) {
				const initSql = createInitialBaseMetadataSql(baseSpec);
				await db.execute(initSql.statement, initSql.params);
			}
			await db.execute("COMMIT");
		} catch (error) {
			await db.execute("ROLLBACK");
			throw error;
		}
	}
}

export async function runGetBaseMetadata(
	db: BunDatabase | TauriDatabase
): Promise<BaseMetadataRow> {
	const { statement } = sql`SELECT * FROM ${sql.id(BASE_METADATA_TABLE_NAME)}`;
	const row = "prepare" in db ? db.prepare(statement).get() : (await db.select(statement))?.[0];
	row.baseSpec = JSON.parse(row.baseSpec);
	return baseMetadataRow.parseAsync(row);
}

/**
 * Creates a SQL statement that inserts the initial metadata values into the base metadata table.
 * Uses INSERT OR IGNORE to ensure we only insert if the table is empty.
 */
export function createInitialBaseMetadataSql(baseSpec: BaseSpec): SqlString {
	return sql`
	INSERT OR IGNORE INTO ${sql.id(BASE_METADATA_TABLE_NAME)} (id, epoch, baseSpec)
	VALUES (${sql.lit(BASE_METADATA_SINGLETON_RECORD_ID)}, 0, ${sql.quote(JSON.stringify(baseSpec))});
`;
}

/**
 * Creates a SQL statement that updates the epoch in the base metadata table.
 */
export function updateEpochSql(epoch: number): SqlString {
	return sql`
	UPDATE ${sql.id(BASE_METADATA_TABLE_NAME)}
	SET epoch = ${epoch}, updatedAt = CURRENT_TIMESTAMP
	WHERE id = ${sql.lit(BASE_METADATA_SINGLETON_RECORD_ID)};
`;
}

/**
 * Creates a SQL statement that updates the schema version in the base metadata table.
 */
export function updateBaseSpecSql(baseSpec: BaseSpec): SqlString {
	return sql`
    UPDATE ${sql.id(BASE_METADATA_TABLE_NAME)}
    SET baseSpec = ${canonicalJsonString(baseSpec)}, updatedAt = CURRENT_TIMESTAMP
    WHERE id = ${sql.lit(BASE_METADATA_SINGLETON_RECORD_ID)};
  `;
}
